'use client';

import cn from 'clsx';
import { forwardRef, InputHTMLAttributes, ReactNode } from 'react';

export interface TextFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  error?: boolean;
  action?: boolean;
  disabled?: boolean;
  masked?: boolean;
  trailingElement?: ReactNode;
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  ({ id, type, error, action, disabled, className, trailingElement, masked, ...props }, ref) => {
    const renderTrailingElement = () => {
      if (action) {
        return (
          <div className="relative h-6 w-[31px]">
            <div className="absolute left-0 top-1/2 -translate-y-1/2">{trailingElement}</div>
          </div>
        );
      }
      return trailingElement;
    };

    return (
      <label
        htmlFor={id}
        className={cn(
          className,
          'flex items-center gap-normal-small rounded-bzc-small border-[1px] px-normal-medium py-normal-small caret-primary-foreground-main',
          {
            'bg-neutral-background-disabled-low': disabled,
            'bg-neutral-background-main': !disabled,
            'border-error-border-main focus-within:border-error-border-main': error,
            'border-neutral-border-high focus-within:border-neutral-border-highest': !error
          }
        )}
      >
        <input
          ref={ref}
          id={id}
          type={masked ? 'password' : type}
          disabled={disabled}
          className={cn(
            'body-medium-medium w-full bg-none min-h-6 text-start text-neutral-foreground-main placeholder:text-neutral-foreground-lowest disabled:bg-neutral-background-disabled-low disabled:text-neutral-foreground-low disabled:placeholder:text-neutral-foreground-lowest',
            {
              'cursor-pointer': type === 'button'
            }
          )}
          {...props}
        />
        {trailingElement && <div className="shrink-0">{renderTrailingElement()}</div>}
      </label>
    );
  }
);

TextField.displayName = 'TextField';
